/**
 * components/Settings/AI/ValidationPrivacyStep.tsx
 * Validation and privacy step for the AI setup wizard
 *
 * Final step of the wizard — validates the API key with visual feedback
 * (loading, success, failure states) and presents a privacy explanation.
 * The "Finish Setup" button is enabled only after successful validation.
 *
 * Work Unit: WU01 - AI Setup Wizard
 * Task: T04 - Validation & Privacy Step
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Shield, XCircle } from 'lucide-react';

// 2. Workspace packages
import { Button, Spinner } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import { aiProviderManager } from '@/services/ai';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Connection description per provider for privacy explanation */
const CONNECTION_DESCRIPTION_MAP: Readonly<Record<AIProviderType, string>> = {
  anthropic: 'via our secure proxy',
  openai: 'directly to OpenAI',
};

/** Privacy bullet points (provider-agnostic items) */
const PRIVACY_POINTS = [
  'Your API key is encrypted and stored on this device only',
  'Trading data never leaves your browser',
  // Third point is provider-specific — rendered separately
  'We never see, store, or have access to your trading data',
] as const;

// ─── Types ──────────────────────────────────────────────────────────────────

/** Validation state machine */
type ValidationState = 'idle' | 'validating' | 'success' | 'error';

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the Validation & Privacy Step
 *
 * @see ValidationPrivacyStep
 */
export interface ValidationPrivacyStepProps {
  /** The provider selected in step 1 */
  selectedProvider: AIProviderType;

  /** The API key entered in step 2 */
  apiKey: string;

  /** Called when the user clicks "Finish Setup" — saves credential and completes wizard */
  onComplete: () => Promise<void>;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Validation & Privacy Step
 *
 * Validates the API key via `aiProviderManager.validateKey()` with
 * loading, success, and failure states. Displays a privacy explanation
 * and a "Finish Setup" button enabled only after successful validation.
 *
 * - Validation is unmount-safe via a mounted ref
 * - Double-click protection via `isLoading` / `isFinishing` states
 * - Re-validation is supported after failure
 *
 * @example
 * ```tsx
 * <ValidationPrivacyStep
 *   selectedProvider="openai"
 *   apiKey="sk-..."
 *   onComplete={handleComplete}
 * />
 * ```
 */
export const ValidationPrivacyStep: React.FC<ValidationPrivacyStepProps> = ({
  selectedProvider,
  apiKey,
  onComplete,
}) => {
  // ─── State ────────────────────────────────────────────────────────────

  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFinishing, setIsFinishing] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ─── Unmount Safety ───────────────────────────────────────────────────

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Derived ──────────────────────────────────────────────────────────

  const connectionDescription = CONNECTION_DESCRIPTION_MAP[selectedProvider];

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleValidate = useCallback(async (): Promise<void> => {
    setValidationState('validating');
    setErrorMessage('');

    try {
      const result = await aiProviderManager.validateKey(
        selectedProvider,
        apiKey,
      );

      if (!mountedRef.current) return;

      if (result.valid) {
        setValidationState('success');
      } else {
        setValidationState('error');
        setErrorMessage(
          result.error ?? 'Invalid API key. Please check and re-enter.',
        );
      }
    } catch {
      if (!mountedRef.current) return;

      setValidationState('error');
      setErrorMessage(
        'Could not connect. Check your internet connection and try again.',
      );
    }
  }, [selectedProvider, apiKey]);

  const handleFinish = useCallback(async (): Promise<void> => {
    setIsFinishing(true);
    setSaveError('');

    try {
      await onComplete();
    } catch {
      if (!mountedRef.current) return;
      setIsFinishing(false);
      setSaveError(
        'Failed to save your API key. Please try again.',
      );
    }
  }, [onComplete]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Validation Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Validate Your API Key</h3>

        {/* Validation Status */}
        <div className="flex items-center gap-3">
          {validationState === 'idle' && (
            <Button
              color="primary"
              variant="flat"
              onPress={handleValidate}
            >
              Validate Key
            </Button>
          )}

          {validationState === 'validating' && (
            <div className="flex items-center gap-2">
              <Spinner size="sm" color="primary" />
              <span className="text-sm text-default-500">
                Testing your API key...
              </span>
            </div>
          )}

          {validationState === 'success' && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-5 text-success" />
              <span className="text-sm font-medium text-success">
                Key validated successfully
              </span>
            </div>
          )}

          {validationState === 'error' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <XCircle className="size-5 text-danger" />
                <span className="text-sm text-danger">{errorMessage}</span>
              </div>
              <Button
                size="sm"
                variant="flat"
                color="danger"
                onPress={handleValidate}
              >
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Privacy Explanation */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="size-5 text-default-500" />
          <h4 className="text-base font-semibold">Your Data Stays Private</h4>
        </div>

        <ul className="space-y-2 pl-1">
          {PRIVACY_POINTS.slice(0, 2).map((point) => (
            <li key={point} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
              <span className="text-sm text-default-600">{point}</span>
            </li>
          ))}

          {/* Provider-specific connection point */}
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
            <span className="text-sm text-default-600">
              AI requests go {connectionDescription}
            </span>
          </li>

          {PRIVACY_POINTS.slice(2).map((point) => (
            <li key={point} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
              <span className="text-sm text-default-600">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Finish Button */}
      <div className="space-y-2">
        {saveError && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-4 shrink-0 text-danger" />
            <span className="text-sm text-danger">{saveError}</span>
          </div>
        )}
        <div className="flex justify-end">
          <Button
            color="primary"
            isDisabled={validationState !== 'success'}
            isLoading={isFinishing}
            onPress={handleFinish}
            className="w-full sm:w-auto"
          >
            Finish Setup
          </Button>
        </div>
      </div>
    </div>
  );
};
